import { Badge, Box } from '@mui/material'
import { useTranslate } from 'ra-core'
import { type ComponentType, Fragment } from 'react'
import { MenuItemLink, usePermissions, useResourceDefinitions, useSidebarState } from 'react-admin'
import { useMatch } from 'react-router-dom'

import { useAccounts } from '../api/accountsProvider'
import { SYSTEM_PERMISSION_LIST, SYSTEM_PERMISSION_READ, UI_PERMISSION_MENU } from '../config/permissions'
import { hasResourcePermission, useResourcePermissions } from '../domain/permissions'
import { AccessGroupsIcon, DefaultResourceIcon, getOutlinedIcon, StatisticsIcon, TimelineIcon } from './icons/menuIcons'
import { AccessGroupsPage } from '../resources/accessGroups/page'
import { STATISTICS_KEY, StatisticsPage } from '../resources/statistics/page'
import vehiclesConfig from '../resources/vehicles/config'
import { TIMELINE_KEY, VehiclesTimelinePage } from '../resources/vehicles/timeline'

interface SectionIconProps {
  icon: ComponentType
  to: string
  badgeCount?: number
}

const SectionIcon = ({ icon, to, badgeCount }: SectionIconProps) => {
  const isActive = Boolean(useMatch({ path: to, end: false }))
  const Icon = isActive ? icon : (getOutlinedIcon(icon) ?? icon)
  if (badgeCount === undefined) {
    return <Icon />
  }
  return (
    <Badge badgeContent={badgeCount} color="error" overlap="rectangular">
      <Icon />
    </Badge>
  )
}

const Menu = () => {
  const { currentAccount } = useAccounts()
  const [sidebarIsOpen] = useSidebarState()
  const translate = useTranslate()

  const resourcesDefinitions = useResourceDefinitions()
  const resources = Object.values(resourcesDefinitions)
  const { permissions } = usePermissions()
  const displayedResources = resources.filter((resource) =>
    hasResourcePermission(permissions, resource.name, SYSTEM_PERMISSION_READ, UI_PERMISSION_MENU),
  )
  const [hasReadForVehicles] = useResourcePermissions(vehiclesConfig.name, SYSTEM_PERMISSION_READ)
  const hasStatisticsMenu = hasResourcePermission(
    permissions,
    STATISTICS_KEY,
    SYSTEM_PERMISSION_READ,
    UI_PERMISSION_MENU,
  )
  const hasGroupsMenu = ['user-groups', 'vehicle-groups'].some((name) =>
    hasResourcePermission(permissions, name, SYSTEM_PERMISSION_READ, SYSTEM_PERMISSION_LIST, UI_PERMISSION_MENU),
  )
  const menuResources = displayedResources.filter(
    (resource) => !['user-groups', 'vehicle-groups'].includes(resource.name),
  )
  const groupsMenuAfter = ['vehicles', 'users'].find((name) => menuResources.some((resource) => resource.name === name))

  const getMenuItemLinkCommonProps = (name: string) => ({
    primaryText: sidebarIsOpen ? name : '',
    sx: { marginBottom: '5px', paddingRight: sidebarIsOpen ? undefined : 0 },
    tooltipProps: { title: name },
  })
  const groupsMenuItem = hasGroupsMenu && (
    <MenuItemLink
      {...getMenuItemLinkCommonProps(translate('mymove.accessGroups.title'))}
      leftIcon={<SectionIcon icon={AccessGroupsIcon} to={AccessGroupsPage.path} />}
      to={AccessGroupsPage.path}
    />
  )

  const getBadgeCount = (resourceName: string): number | undefined => {
    const counts = currentAccount?.counts
    return (
      counts?.[resourceName] ??
      counts?.[resourceName.replace(/-/g, '_')] ??
      (resourceName === 'incident-reports' ? counts?.['damage-reports'] : undefined)
    )
  }

  return (
    <Box mt={1}>
      {hasReadForVehicles && (
        <MenuItemLink
          {...getMenuItemLinkCommonProps(translate('mymove.scheduler.name'))}
          key={TIMELINE_KEY}
          leftIcon={<SectionIcon icon={TimelineIcon} to={VehiclesTimelinePage.path} />}
          to={VehiclesTimelinePage.path}
        />
      )}
      {hasStatisticsMenu && (
        <MenuItemLink
          {...getMenuItemLinkCommonProps(translate('mymove.statistics.name'))}
          key={STATISTICS_KEY}
          leftIcon={<SectionIcon icon={StatisticsIcon} to={StatisticsPage.path} />}
          to={StatisticsPage.path}
        />
      )}
      {!groupsMenuAfter && groupsMenuItem}
      {menuResources.map((resource) => {
        const to = `/${resource.name}`
        const hasBadge = Boolean(currentAccount && resource.options?.badge)
        return (
          <Fragment key={resource.name}>
            <MenuItemLink
              {...getMenuItemLinkCommonProps(translate(`resources.${resource.name}.name`, 2))}
              leftIcon={
                <SectionIcon
                  icon={resource.icon ?? DefaultResourceIcon}
                  to={to}
                  badgeCount={hasBadge ? getBadgeCount(resource.name) : undefined}
                />
              }
              to={to}
            />
            {resource.name === groupsMenuAfter && groupsMenuItem}
          </Fragment>
        )
      })}
    </Box>
  )
}

export default Menu
